/**
 * Print unit math — the ONE owner of the paper ↔ world ↔ point ↔ pixel
 * conversions, so the PDF backend, the SVG preview and the raster renderer
 * agree to the micrometer.
 *
 * Conventions: paper lengths are millimeters, world lengths are meters, a
 * drawing scale is stored as its denominator (`1:100` → `100`).
 */

export const MM_PER_INCH = 25.4;
export const PT_PER_INCH = 72;
/** One PostScript point in millimeters (≈ 0.3528). */
export const MM_PER_PT = MM_PER_INCH / PT_PER_INCH;

export function mmToPt(mm: number): number {
  return mm / MM_PER_PT;
}

export function ptToMm(pt: number): number {
  return pt * MM_PER_PT;
}

/** World meters → paper millimeters at `1:scaleDen`. */
export function worldToPaperMm(meters: number, scaleDen: number): number {
  return (meters * 1000) / scaleDen;
}

/** Paper millimeters → world meters at `1:scaleDen`. */
export function paperMmToWorld(mm: number, scaleDen: number): number {
  return (mm * scaleDen) / 1000;
}

/** Pixel count that covers `mm` of paper at `dpi` — the exact raster size of a viewport. */
export function rasterPx(mm: number, dpi: number): number {
  return Math.max(1, Math.round((mm / MM_PER_INCH) * dpi));
}

/** Common architectural scales, ascending denominators. */
export const STANDARD_SCALES: readonly number[] = [1, 2, 5, 10, 20, 25, 50, 75, 100, 125, 200, 250, 500, 1000, 2000, 2500];

/** Raster resolutions offered in the UI. */
export const STANDARD_DPIS: readonly number[] = [72, 150, 200, 300, 400, 600];

export function formatScale(scaleDen: number): string {
  const rounded = Math.round(scaleDen * 100) / 100;
  return `1:${rounded}`;
}

/**
 * Smallest standard scale (largest drawing) at which `worldW × worldH`
 * meters fit inside `paperW × paperH` millimeters, or the coarsest standard
 * scale when nothing fits.
 */
export function fitStandardScale(worldW: number, worldH: number, paperW: number, paperH: number): number {
  for (const den of STANDARD_SCALES) {
    if (worldToPaperMm(worldW, den) <= paperW && worldToPaperMm(worldH, den) <= paperH) return den;
  }
  return STANDARD_SCALES[STANDARD_SCALES.length - 1];
}

// ─── Text size ──────────────────────────────────────────────────────────

export type TextSizeUnit = 'pt' | 'mm';

/**
 * What a text size measures. `em` is the typographic size (what "10 pt" means
 * in any word processor); `cap` is the height of a capital letter, which is
 * how technical lettering is specified (ISO 3098: 2.5 / 3.5 / 5 mm). 10 pt em
 * is 3.53 mm, but its capitals are only ≈ 2.5 mm tall — so "exact text size"
 * needs the basis spelled out.
 */
export type TextSizeBasis = 'em' | 'cap';

export interface TextSize {
  value: number;
  unit: TextSizeUnit;
  basis: TextSizeBasis;
}

/** Cap height / em of a typical sans; used when the real font metrics are not loaded (preview fallbacks). */
export const DEFAULT_CAP_HEIGHT_RATIO = 0.7;

/** The size as millimeters of whichever basis it was given in. */
function textSizeMm(size: TextSize): number {
  return size.unit === 'pt' ? ptToMm(size.value) : size.value;
}

/** Em size in millimeters on paper — what the PDF / SVG font size is set to. */
export function textSizeToEmMm(size: TextSize, capHeightRatio: number = DEFAULT_CAP_HEIGHT_RATIO): number {
  const mm = textSizeMm(size);
  return size.basis === 'em' ? mm : mm / capHeightRatio;
}

/** Em size in points — the value handed to the PDF `Tf` operator. */
export function textSizeToEmPt(size: TextSize, capHeightRatio: number = DEFAULT_CAP_HEIGHT_RATIO): number {
  return mmToPt(textSizeToEmMm(size, capHeightRatio));
}

/** Height of a capital letter on paper, in millimeters. */
export function textSizeToCapMm(size: TextSize, capHeightRatio: number = DEFAULT_CAP_HEIGHT_RATIO): number {
  const mm = textSizeMm(size);
  return size.basis === 'cap' ? mm : mm * capHeightRatio;
}

/** Real-world height of the capitals of plan text drawn at `1:scaleDen` — the "= 0.25 m in reality" hint. */
export function textSizeToWorldCapHeight(size: TextSize, scaleDen: number, capHeightRatio: number = DEFAULT_CAP_HEIGHT_RATIO): number {
  return paperMmToWorld(textSizeToCapMm(size, capHeightRatio), scaleDen);
}

// ─── Length units of printed values (the user's preference) ─────────

/** Unit printed lengths are written in (geometry and scales stay in meters). */
export type PrintLengthUnit = 'm' | 'ft';
export const FEET_PER_METER = 3.28084;

/** `meters` in `unit`. */
export function lengthIn(meters: number, unit: PrintLengthUnit = 'm'): number {
  return unit === 'ft' ? meters * FEET_PER_METER : meters;
}

/** "3.73 m" / "12.24 ft". */
export function formatLength(meters: number, unit: PrintLengthUnit = 'm', decimals = 2): string {
  return `${lengthIn(meters, unit).toFixed(decimals)} ${unit}`;
}

